// frontend/src/components/chat/NotificationSettings.jsx
// Notification settings panel for chat

import React, { useState, useEffect } from "react";
import notificationService from "../../services/notificationService";

const NotificationSettings = ({ onClose }) => {
  const [status, setStatus] = useState(notificationService.getStatus());
  const [isRequesting, setIsRequesting] = useState(false);

  // Update status when component mounts
  useEffect(() => {
    setStatus(notificationService.getStatus());
  }, []);

  // Request notification permission
  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      await notificationService.requestPermission();
      setStatus(notificationService.getStatus());
    } finally {
      setIsRequesting(false);
    }
  };

  // Toggle sound
  const handleToggleSound = () => {
    notificationService.setSoundEnabled(!status.soundEnabled);
    setStatus(notificationService.getStatus());
  };

  // Test notification
  const handleTestNotification = () => {
    notificationService.showNotification({
      title: "Test Notification",
      body: "Notifications are working!",
      senderName: "System",
    });
  };

  return (
    <div className="notification-settings">
      <div className="notification-settings-header">
        <h6 className="mb-0">
          <i className="bi bi-bell me-2"></i>
          Notification Settings
        </h6>
        {onClose && (
          <button
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
          ></button>
        )}
      </div>

      <div className="notification-settings-body">
        {/* Browser Support Check */}
        {!status.isSupported ? (
          <div className="alert alert-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Your browser doesn't support notifications.
          </div>
        ) : (
          <>
            {/* Permission Status */}
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Desktop Notifications</div>
                <div className="setting-description">
                  Get notified when you receive new messages
                </div>
              </div>
              <div className="setting-action">
                {status.permission === "granted" ? (
                  <span className="badge bg-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Enabled
                  </span>
                ) : status.permission === "denied" ? (
                  <span className="badge bg-danger">
                    <i className="bi bi-x-circle me-1"></i>
                    Blocked
                  </span>
                ) : (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleRequestPermission}
                    disabled={isRequesting}
                  >
                    {isRequesting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Requesting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-bell me-1"></i>
                        Enable
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Blocked Message */}
            {status.permission === "denied" && (
              <div className="alert alert-info small mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Notifications are blocked. To enable them, click the lock icon
                in your browser's address bar and allow notifications for this
                site.
              </div>
            )}

            {/* Sound Toggle */}
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Notification Sound</div>
                <div className="setting-description">
                  Play a sound when messages arrive
                </div>
              </div>
              <div className="setting-action">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="soundToggle"
                    checked={status.soundEnabled}
                    onChange={handleToggleSound}
                  />
                  <label
                    className="form-check-label visually-hidden"
                    htmlFor="soundToggle"
                  >
                    Sound enabled
                  </label>
                </div>
              </div>
            </div>

            {/* Test Notification */}
            {status.isEnabled && (
              <div className="setting-item">
                <div className="setting-info">
                  <div className="setting-label">Test Notification</div>
                  <div className="setting-description">
                    Send a test notification to verify it's working
                  </div>
                </div>
                <div className="setting-action">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleTestNotification}
                  >
                    <i className="bi bi-send me-1"></i>
                    Test
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* HIPAA Notice */}
        <div className="hipaa-notice mt-3">
          <i className="bi bi-shield-lock me-1"></i>
          <small>
            HIPAA Compliance: Notification content does not include message
            details or PHI.
          </small>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
